import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Grid, InputAdornment, MenuItem, Paper, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EuroIcon from '@mui/icons-material/Euro';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useNavigate } from 'react-router-dom';
import { getDatosContables, postGasto } from '../../services/apis';
import { getCookie } from '../../utils/cookies';
import { optimizarImagenWebp } from '../../utils/optimizarImagen';

const initialForm = { nombre: '', observacion: '', cantidad: '', id_usuario: '', id_apartado: '', foto: null };

export const CrearGasto = () => {
    const navigate = useNavigate();
    const [year, setYear] = useState(new Date().getFullYear());
    const [form, setForm] = useState(initialForm);
    const [usuarios, setUsuarios] = useState([]);
    const [conceptos, setConceptos] = useState([]);
    const [anios, setAnios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');
    const [esAdministrador, setEsAdministrador] = useState(false);

    useEffect(() => {
        const cargar = async () => {
            setLoading(true);
            setError('');
            const response = await getDatosContables(year);
            const data = response?.data;
            if (!data?.success) {
                setError('No s’han pogut carregar els conceptes comptables.');
                setLoading(false);
                return;
            }

            const listaConceptos = (data.data || [])
                .filter((apartado) => Number(apartado.activo ?? 1) === 1)
                .map((apartado) => ({ id: apartado.id_apartado, nombre: apartado.apartado }));
            const listaUsuarios = data.usuarios || [];
            const listaAnios = (data.anios || []).map((anyo) => Number(anyo.anyo));
            const usuarioActualId = data.usuario_actual || '';
            const administrador = ['901', '986'].includes(String(data.rol_actual || ''));
            setConceptos(listaConceptos);
            setUsuarios(listaUsuarios);
            setAnios(listaAnios);
            setEsAdministrador(administrador);
            setForm((prev) => ({
                ...prev,
                id_apartado: listaConceptos.some((concepto) => String(concepto.id) === String(prev.id_apartado)) ? prev.id_apartado : (listaConceptos[0]?.id || ''),
                id_usuario: listaUsuarios.some((usuario) => String(usuario.id_usuario) === String(prev.id_usuario)) ? prev.id_usuario : (listaUsuarios.find((usuario) => String(usuario.id_usuario) === String(usuarioActualId))?.id_usuario || listaUsuarios[0]?.id_usuario || '')
            }));
            setLoading(false);
        };

        cargar();
    }, [year]);

    const actualizar = (event) => setForm((prev) => ({ ...prev, [event.target.name]: event.target.value }));

    const seleccionarFoto = async (event) => {
        const file = event.target.files?.[0] || null;
        if (!file) return setForm((prev) => ({ ...prev, foto: null }));
        try {
            const fotoOptimizada = await optimizarImagenWebp(file);
            setForm((prev) => ({ ...prev, foto: fotoOptimizada }));
        } catch {
            setForm((prev) => ({ ...prev, foto: file }));
        }
    };

    const guardar = async () => {
        if (!form.nombre.trim() || !form.id_apartado || !form.id_usuario || form.cantidad === '' || Number(form.cantidad) < 0) {
            setError('Completa el concepte, qui l’ha afegida i un import vàlid.');
            return;
        }

        const payload = new FormData();
        payload.append('token', getCookie('PHPSESSID'));
        payload.append('asunto', form.nombre.trim());
        payload.append('descripcion', form.observacion.trim());
        payload.append('cantidad', form.cantidad);
        payload.append('id_usuario', form.id_usuario);
        payload.append('id_apartado', form.id_apartado);
        payload.append('year', year);
        if (form.foto) payload.append('foto', form.foto);

        setGuardando(true);
        setError('');
        const response = await postGasto(payload);
        setGuardando(false);
        if (response?.success) {
            navigate('/contabilidad');
        } else {
            setError(response?.message || 'No s’ha pogut guardar la despesa.');
        }
    };

    return (
        <Box className="accounting-shell expense-page-shell">
            <Paper elevation={0} className="accounting-header expense-page-header">
                <Box className="expense-dialog-title">
                    <Box className="expense-dialog-icon"><ReceiptLongIcon /></Box>
                    <Box className="expense-dialog-heading">
                        <Typography className="accounting-eyebrow">Comptabilitat</Typography>
                        <Typography variant="h4" className="accounting-title">Afegir despesa</Typography>
                    </Box>
                </Box>
                <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/contabilidad')}>Tornar</Button>
            </Paper>

            <Paper elevation={0} className="expense-page-form">
                {error && <Alert severity="error">{error}</Alert>}
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}><TextField select fullWidth label="Any" value={year} onChange={(event) => setYear(Number(event.target.value))}>{(anios.length ? anios : [year]).map((anyo) => <MenuItem key={anyo} value={anyo}>{anyo}</MenuItem>)}</TextField></Grid>
                    <Grid size={{ xs: 12, sm: 8 }}><TextField select required fullWidth label="Concepte comptable" name="id_apartado" value={form.id_apartado} onChange={actualizar} disabled={loading}>{conceptos.map((concepto) => <MenuItem key={concepto.id} value={concepto.id}>{concepto.nombre}</MenuItem>)}</TextField></Grid>
                    <Grid size={{ xs: 12 }}><TextField required fullWidth label="Nom de la despesa" name="nombre" value={form.nombre} onChange={actualizar} inputProps={{ maxLength: 80 }} /></Grid>
                    <Grid size={{ xs: 12 }}><TextField fullWidth multiline minRows={3} label="Observació" name="observacion" value={form.observacion} onChange={actualizar} inputProps={{ maxLength: 500 }} /></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><TextField required select fullWidth label="Afegit per" name="id_usuario" value={form.id_usuario} onChange={actualizar} disabled={loading || !esAdministrador}>{usuarios.map((usuario) => <MenuItem key={usuario.id_usuario} value={usuario.id_usuario}>{usuario.nombre_usuario}</MenuItem>)}</TextField></Grid>
                    <Grid size={{ xs: 12, sm: 3 }}><TextField required fullWidth label="Import" name="cantidad" type="number" value={form.cantidad} onChange={actualizar} inputProps={{ min: '0', step: '0.01' }} InputProps={{ startAdornment: <InputAdornment position="start"><EuroIcon /></InputAdornment> }} /></Grid>
                    <Grid size={{ xs: 12, sm: 3 }}><Button fullWidth variant="outlined" component="label" className="expense-upload-button"><AttachFileIcon />{form.foto ? form.foto.name : 'Adjuntar foto'}<input type="file" hidden accept="image/*" onChange={seleccionarFoto} /></Button></Grid>
                </Grid>
                <Box className="expense-page-actions"><Button variant="outlined" onClick={() => navigate('/contabilidad')}>Cancel·lar</Button><Button variant="contained" startIcon={<ReceiptLongIcon />} onClick={guardar} disabled={loading || guardando}>{guardando ? 'Guardant...' : 'Guardar despesa'}</Button></Box>
            </Paper>
        </Box>
    );
};
